import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Edit } from 'lucide-react';

interface Restaurant {
  restaurant_id: string;
  restaurant_name: string;
  address: string | null;
  status: string | null;
  logo_url: string | null;
  primary_color: string | null;
  secondary_color: string | null;
  ubereats_commission_rate: number | null;
  ubereats_markup: number | null;
  google_review_url: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  ordering_page_url: string | null;
  review_page_url: string | null;
  organisation_id: string | null;
}

interface RestaurantTableProps {
  restaurants: Restaurant[];
  loading: boolean;
  onEditRestaurant: (restaurant: Restaurant) => void;
}

const RestaurantTable: React.FC<RestaurantTableProps> = ({
  restaurants,
  loading,
  onEditRestaurant,
}) => {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array(3).fill(0).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 border rounded-md">
            <div className="flex-1">
              <Skeleton className="h-5 w-48 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
            <Skeleton className="h-8 w-16" />
          </div>
        ))}
      </div>
    );
  }

  // Truncate address to 60 characters
  const truncateAddress = (address: string | null): string => {
    if (!address) return '—';
    return address.length > 60 ? `${address.substring(0, 60)}...` : address;
  };

  return (
    <div className="border rounded-md">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Restaurant Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Address</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {restaurants.map((restaurant) => (
            <TableRow key={restaurant.restaurant_id} className="group">
              <TableCell>
                <button
                  onClick={() => onEditRestaurant(restaurant)}
                  className="font-medium cursor-pointer transition-colors hover:text-primary active:text-primary"
                >
                  {restaurant.restaurant_name}
                </button>
              </TableCell>
              <TableCell>
                <Badge variant={restaurant.status === 'active' ? 'default' : 'secondary'}>
                  {restaurant.status || 'active'}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {truncateAddress(restaurant.address)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditRestaurant(restaurant)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Edit className="h-4 w-4" />
                  <span className="sr-only">Edit {restaurant.restaurant_name}</span>
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default RestaurantTable;
